import { useState, useEffect, useRef } from 'react';
import { Download, Square } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Slider } from '@/components/ui/Slider';
import { Select } from '@/components/ui/Select';
import { Dropzone } from '@/components/ui/Dropzone';
import { useApp } from '@/store/app';
import { downloadCanvas, loadImage, readFileAsDataURL } from '@/lib/utils';
import { toast } from 'sonner';

const RATIOS = [
  { id: 'free', label: 'Free', w: 0, h: 0 },
  { id: '1:1', label: '1:1', w: 1, h: 1 },
  { id: '16:9', label: '16:9', w: 16, h: 9 },
  { id: '9:16', label: '9:16', w: 9, h: 16 },
  { id: '4:3', label: '4:3', w: 4, h: 3 },
  { id: '3:2', label: '3:2', w: 3, h: 2 },
];

export function ImageCrop() {
  const { pendingFile, setPendingFile } = useApp();
  const [file, setFile] = useState<File | null>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [ratio, setRatio] = useState('free');
  const [crop, setCrop] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [zoom, setZoom] = useState(100);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (pendingFile && pendingFile.type.startsWith('image/')) {
      handleFile([pendingFile]);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFile(files: File[]) {
    const f = files[0];
    if (!f) return;
    setFile(f);
    const url = await readFileAsDataURL(f);
    const im = await loadImage(url);
    setImg(im);
    setCrop({
      x: Math.round(im.width * 0.1),
      y: Math.round(im.height * 0.1),
      w: Math.round(im.width * 0.8),
      h: Math.round(im.height * 0.8),
    });
  }

  // Lock to ratio
  useEffect(() => {
    const r = RATIOS.find((x) => x.id === ratio);
    if (!r || r.w === 0 || !img) return;
    const newH = Math.round((crop.w * r.h) / r.w);
    if (newH !== crop.h && newH <= img.height - crop.y) {
      setCrop((c) => ({ ...c, h: newH }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratio, crop.w]);

  // Render
  useEffect(() => {
    if (!img || !canvasRef.current) return;
    const c = canvasRef.current;
    const scale = zoom / 100;
    c.width = img.width * scale;
    c.height = img.height * scale;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img, 0, 0, c.width, c.height);
    // dim outside crop
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.clearRect(crop.x * scale, crop.y * scale, crop.w * scale, crop.h * scale);
    ctx.drawImage(
      img,
      crop.x,
      crop.y,
      crop.w,
      crop.h,
      crop.x * scale,
      crop.y * scale,
      crop.w * scale,
      crop.h * scale,
    );
    // outline
    ctx.strokeStyle = '#a78bfa';
    ctx.lineWidth = 2;
    ctx.strokeRect(crop.x * scale, crop.y * scale, crop.w * scale, crop.h * scale);
  }, [img, crop, zoom]);

  function exportCrop() {
    if (!img || !file) return;
    const c = document.createElement('canvas');
    c.width = crop.w;
    c.height = crop.h;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img, crop.x, crop.y, crop.w, crop.h, 0, 0, crop.w, crop.h);
    const base = file.name.replace(/\.\w+$/, '');
    downloadCanvas(c, `${base}-crop.png`);
    toast.success('Saved');
  }

  return (
    <div>
      <ToolHeader
        toolId="image-crop"
        title="Image crop"
        description="Crop images to a free area or fixed aspect ratio. Pixel-precise."
      />

      {!img ? (
        <Dropzone accept="image/*" onFiles={handleFile} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:order-2 space-y-3">
            <Field label="Aspect ratio">
              <Select
                value={ratio}
                onChange={(e) => setRatio(e.target.value)}
                options={RATIOS.map((r) => ({ value: r.id, label: r.label }))}
              />
            </Field>
            <Field label="X" hint={`${crop.x}px`}>
              <Slider
                value={crop.x}
                onChange={(v) => setCrop((c) => ({ ...c, x: Math.min(v, img.width - c.w) }))}
                max={Math.max(0, img.width - crop.w)}
                showValue={false}
              />
            </Field>
            <Field label="Y" hint={`${crop.y}px`}>
              <Slider
                value={crop.y}
                onChange={(v) => setCrop((c) => ({ ...c, y: Math.min(v, img.height - c.h) }))}
                max={Math.max(0, img.height - crop.h)}
                showValue={false}
              />
            </Field>
            <Field label="Width" hint={`${crop.w}px`}>
              <Slider
                value={crop.w}
                onChange={(v) => setCrop((c) => ({ ...c, w: Math.min(v, img.width - c.x) }))}
                min={20}
                max={img.width}
                showValue={false}
              />
            </Field>
            <Field label="Height" hint={`${crop.h}px`}>
              <Slider
                value={crop.h}
                onChange={(v) => setCrop((c) => ({ ...c, h: Math.min(v, img.height - c.y) }))}
                min={20}
                max={img.height}
                showValue={false}
              />
            </Field>
            <Field label="Preview zoom" hint={`${zoom}%`}>
              <Slider value={zoom} onChange={setZoom} min={20} max={200} showValue={false} />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="secondary" size="sm" onClick={() => { setImg(null); setFile(null); }}>
                New image
              </Button>
              <Button variant="primary" size="sm" onClick={exportCrop}>
                <Download size={11} />
                Export
              </Button>
            </div>
          </Card>
          <Card className="lg:col-span-2 lg:order-1 !p-0 overflow-hidden bg-bg-inset">
            <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
              Preview
            </div>
            <div className="p-6 flex items-center justify-center min-h-[420px] overflow-auto">
              <canvas ref={canvasRef} className="max-w-full rounded shadow-card" />
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

export default ImageCrop;
